import React from "react";
import { useLanguage } from "../context/LanguageContext";
import SEO from "../components/SEO";
import { siteConfig } from "../config/siteConfig";
import {
  Globe,
  Handshake,
  FileCheck,
  MessageCircle,
  Phone,
  Mail,
  ShieldCheck,
} from "lucide-react";

const ForInternationalSuppliers = () => {
  const { t } = useLanguage();

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://skwoolens.trivanahospitality.com/en"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "For International Suppliers",
        "item": "https://skwoolens.trivanahospitality.com/en/for-international-suppliers"
      }
    ]
  };

  return (
    <div className="space-y-16 sm:space-y-24 py-6">
      <SEO
        title="Sell Wool to S.K. Woollens | International Exporter & Supplier Desk"
        description="Establish raw wool sourcing partnerships with S.K. Woollens in India. Open to exporter channels across Australia, New Zealand, China, and Syria."
        keywords="wool exporters to India, become a wool supply partner, Bikaner wool imports, global wool merchants, AWEX, WNZ, Syrian wool trade"
        canonicalUrl="https://skwoolens.trivanahospitality.com/en/for-international-suppliers"
        schema={[breadcrumbSchema]}
      />
      {/* 1. Header Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-wool-sand/50 via-white to-wool-sand/20 border border-wool-border rounded-3xl p-8 sm:p-14 shadow-subtle">
          <div className="max-w-3xl space-y-4">
            <div className="wool-badge">
              <Globe className="w-3.5 h-3.5 text-wool-bronze" />
              <span>International Wool Exporters & Brokers</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-wool-charcoal tracking-tight font-display">
              {t("supplierForm.title")}
            </h1>
            <p className="text-base sm:text-lg text-wool-slate leading-relaxed">
              {t("supplierForm.subtitle")}
            </p>

            <div className="flex flex-wrap gap-2 pt-2">
              {["Australia", "New Zealand", "China", "Syria", "Other Global Markets"].map((c, i) => (
                <span
                  key={i}
                  className="px-3 py-1 bg-white border border-wool-border text-xs font-semibold text-wool-charcoal rounded-lg shadow-sm"
                >
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 2. Supplier Partnership Value */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="wool-card p-6 space-y-3 bg-white">
            <div className="w-12 h-12 rounded-xl bg-wool-sand/60 flex items-center justify-center border border-wool-border">
              <Handshake className="w-6 h-6 text-wool-bronze" />
            </div>
            <h3 className="text-lg font-bold text-wool-charcoal">
              Direct B2B Indian Market Access
            </h3>
            <p className="text-xs sm:text-sm text-wool-slate leading-relaxed">
              Connect directly with consistent commercial demand from established spinning and carpet manufacturing mills in Northern India.
            </p>
          </div>

          <div className="wool-card p-6 space-y-3 bg-white">
            <div className="w-12 h-12 rounded-xl bg-wool-sand/60 flex items-center justify-center border border-wool-border">
              <FileCheck className="w-6 h-6 text-wool-bronze" />
            </div>
            <h3 className="text-lg font-bold text-wool-charcoal">
              Structured Commercial Terms
            </h3>
            <p className="text-xs sm:text-sm text-wool-slate leading-relaxed">
              Transparent specifications, reliable LC / payment arrangements, and clear communication on clip parameters and lot approvals.
            </p>
          </div>

          <div className="wool-card p-6 space-y-3 bg-white">
            <div className="w-12 h-12 rounded-xl bg-wool-sand/60 flex items-center justify-center border border-wool-border">
              <ShieldCheck className="w-6 h-6 text-wool-bronze" />
            </div>
            <h3 className="text-lg font-bold text-wool-charcoal">
              Since 1965 Trading Legacy
            </h3>
            <p className="text-xs sm:text-sm text-wool-slate leading-relaxed">
              Decades of experience in raw wool valuation, Bikaner market integration, and reliable long-term business partnerships.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white border border-wool-border rounded-3xl p-6 sm:p-12 shadow-premium">
          <div className="max-w-3xl mb-8 space-y-2">
            <div className="wool-badge">
              <Handshake className="w-3.5 h-3.5 text-wool-bronze" />
              <span>Supplier Partnership Desk</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-wool-charcoal font-display">
              Connect with the international sourcing desk
            </h2>
            <p className="text-xs sm:text-sm text-wool-muted">
              Share your wool export details and commercial requirements directly by WhatsApp or phone.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <a
              href="https://wa.me/917014581250?text=Hi%2C%20I%20would%20like%20to%20connect%20with%20you%20regarding%20wool%20sourcing%20and%20supply"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-4 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-2xl text-emerald-800 font-semibold transition"
            >
              <MessageCircle className="w-5 h-5 text-emerald-600 shrink-0" />
              <span>WhatsApp: +91 7014581250</span>
            </a>

            <a
              href={`tel:${siteConfig.contacts.primaryPhone}`}
              className="flex items-center gap-3 p-4 bg-wool-sand/40 hover:bg-wool-sand/70 border border-wool-border rounded-2xl text-wool-charcoal font-semibold transition"
            >
              <Phone className="w-5 h-5 text-wool-bronze shrink-0" />
              <span>Call: +91 {siteConfig.contacts.primaryPhone}</span>
            </a>

            <a
              href={`mailto:${siteConfig.contacts.primaryEmail}`}
              className="flex items-center gap-3 p-4 bg-wool-sand/40 hover:bg-wool-sand/70 border border-wool-border rounded-2xl text-wool-charcoal font-semibold transition md:col-span-2"
            >
              <Mail className="w-5 h-5 text-wool-bronze shrink-0" />
              <span>Email: {siteConfig.contacts.primaryEmail}</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForInternationalSuppliers;
